import React, { useMemo } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ReferenceLine,
} from "recharts";
import { Droplets } from "lucide-react";
import ChartContainer from "./ChartContainer";
import { fmtDate } from "../lib/api";

// Static styles hoisted so Recharts gets stable prop references.
const TICK = { fill: "#94A3B8", fontSize: 11, fontFamily: "JetBrains Mono" };
const LEGEND = { color: "#94A3B8", fontSize: 12 };
const MARGIN = { top: 8, right: 56, left: 0, bottom: 4 };
const TT_STYLE = {
  backgroundColor: "#FCD34D", border: "1px solid #B45309",
  borderRadius: 10, color: "#111827",
  fontFamily: "JetBrains Mono, monospace", fontSize: 12,
};
const TT_LABEL = { color: "#7C2D12", fontWeight: 600 };
const TT_ITEM = { color: "#111827" };
const HB_DOT = { r: 3, strokeWidth: 0, fill: "#EF4444" };
const TS_DOT = { r: 3, strokeWidth: 0, fill: "#F59E0B" };
const HB_ACTIVE = { r: 6 };

/**
 * Iron-Triad combined chart — the most sailing-relevant marker triad.
 *
 * Layout (one ComposedChart, three Y-axes):
 *   - Left axis: Ferritin (ng/ml) -- bars
 *   - Right axis: Haemoglobin (g/dL) -- solid line
 *   - Far right axis (hidden ticks): Transferrin saturation (%) -- dashed line
 *
 * Each axis is auto-zoomed independently so trends stay visible. Reference
 * lines mark the lower limit for Hb (12 g/dL) and Ferritin (30 ng/ml) since
 * "low" is the sailing concern — high iron is rare and not actionable.
 *
 * Props:
 *   records: athlete's lab records (already sorted oldest -> newest)
 *   sailorName: for the chart caption
 */
export default function IronTriadChart({ records, sailorName }) {
  const data = useMemo(() => {
    return (records || [])
      .filter((r) => (r.type || "lab") === "lab" && r.params
        && (r.params.hb != null || r.params.ferritin != null || r.params.ts != null))
      .map((r) => ({
        date: r.date,
        label: fmtDate(r.date),
        hb: r.params.hb ?? null,
        ferritin: r.params.ferritin ?? null,
        ts: r.params.ts ?? null,
      }));
  }, [records]);

  // Compute axis domains up-front (BEFORE the empty-state early return)
  // so the useMemo hooks run in a stable order on every render — React
  // requires hooks to be called unconditionally, and an early `return`
  // above these would break the rule.
  const { ferDomain, hbDomain, tsDomain, hasHb, hasFer, hasTs, hbVals, ferVals, tsVals } = useMemo(() => {
    const _hbVals = data.map((d) => d.hb).filter((v) => v != null);
    const _ferVals = data.map((d) => d.ferritin).filter((v) => v != null);
    const _tsVals = data.map((d) => d.ts).filter((v) => v != null);
    const _hasHb = _hbVals.length > 0;
    const _hasFer = _ferVals.length > 0;
    const _hasTs = _tsVals.length > 0;
    const hbMin = _hasHb ? Math.min(..._hbVals, 11) : 11;
    const hbMax = _hasHb ? Math.max(..._hbVals, 16) : 16;
    const ferMin = _hasFer ? Math.max(0, Math.min(..._ferVals) - 5) : 0;
    const ferMax = _hasFer ? Math.max(..._ferVals, 35) : 35;
    const tsMin = _hasTs ? Math.max(0, Math.min(..._tsVals) - 5) : 0;
    const tsMax = _hasTs ? Math.max(..._tsVals, 50) : 50;
    return {
      ferDomain: [ferMin, ferMax + 5],
      hbDomain: [hbMin - 0.5, hbMax + 0.5],
      tsDomain: [tsMin, tsMax + 5],
      hasHb: _hasHb, hasFer: _hasFer, hasTs: _hasTs,
      hbVals: _hbVals, ferVals: _ferVals, tsVals: _tsVals,
    };
  }, [data]);
  // Hb axis needs a custom tick colour but everything else matches the
  // shared TICK constant — merge once instead of per-render.
  const hbTick = useMemo(() => ({ ...TICK, fill: "#FCA5A5" }), []);

  if (data.length === 0) {
    return (
      <div className="card p-5 md:p-6" data-testid="iron-triad-empty">
        <div className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
          <Droplets size={12} className="text-rose-300" /> Iron Triad
        </div>
        <div className="text-sm text-slate-500 mt-2">
          No Hb / Ferritin / Transferrin Saturation values recorded yet.
        </div>
      </div>
    );
  }

  // Latest values + a one-line status summary the coach reads first.
  const latest = data[data.length - 1];
  const statusBits = [];
  if (latest.hb != null) statusBits.push(`Hb ${latest.hb} g/dL${latest.hb < 12 ? " ⚠" : ""}`);
  if (latest.ferritin != null) statusBits.push(`Ferritin ${latest.ferritin} ng/ml${latest.ferritin < 30 ? " ⚠" : ""}`);
  if (latest.ts != null) statusBits.push(`TS ${latest.ts}%${latest.ts < 15 ? " ⚠" : ""}`);

  return (
    <div className="card p-5 md:p-6" data-testid="iron-triad-chart">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <Droplets size={12} className="text-rose-300" /> Iron Triad
          </div>
          <div className="font-display text-lg text-slate-100 mt-0.5">
            {sailorName ? `${sailorName} · ` : ""}Hb · Ferritin · Transferrin Sat
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Storage iron drives endurance — track all three together. Ferritin (storage) falls first; Hb falls last.
          </div>
        </div>
        <div className="text-right text-[11px] font-mono-data text-slate-300">
          <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-0.5">Latest · {fmtDate(latest.date)}</div>
          {statusBits.map((s) => (
            <div key={s} className={s.includes("⚠") ? "text-rose-300" : "text-slate-200"}>
              {s}
            </div>
          ))}
        </div>
      </div>

      <div style={{ width: "100%", height: 320, minWidth: 0 }}>
        <ChartContainer>
          <ComposedChart data={data} margin={MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E304B" />
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={TICK}
                   interval="preserveStartEnd" minTickGap={20} />
            {/* Left: Ferritin (ng/ml) bars */}
            {hasFer && (
              <YAxis yAxisId="fer" axisLine={false} tickLine={false} tick={TICK}
                     domain={ferDomain}
                     tickFormatter={(v) => `${v} ng/ml`} width={68} />
            )}
            {/* Right: Hb (g/dL) line */}
            {hasHb && (
              <YAxis yAxisId="hb" orientation="right" axisLine={false} tickLine={false}
                     tick={hbTick}
                     domain={hbDomain}
                     tickFormatter={(v) => `${v.toFixed(1)} g/dL`} width={64} />
            )}
            {/* Hidden far-right axis for Transferrin Sat % — keeps the visual
                right edge clean while still scaling the dashed TS line. */}
            {hasTs && (
              <YAxis yAxisId="ts" orientation="right" hide
                     domain={tsDomain} />
            )}
            {/* Reference thresholds — only the LOW edge is clinically actionable
                so we render just one ReferenceLine per metric. */}
            {hasFer && (
              <ReferenceLine yAxisId="fer" y={30} stroke="#F59E0B"
                             strokeDasharray="4 4" strokeOpacity={0.6}
                             label={{ value: "Ferritin low 30", position: "insideLeft",
                                      fill: "#FBBF24", fontSize: 9 }} />
            )}
            {hasHb && (
              <ReferenceLine yAxisId="hb" y={12} stroke="#EF4444"
                             strokeDasharray="4 4" strokeOpacity={0.6}
                             label={{ value: "Hb low 12", position: "insideRight",
                                      fill: "#FCA5A5", fontSize: 9 }} />
            )}
            <Tooltip contentStyle={TT_STYLE} labelStyle={TT_LABEL} itemStyle={TT_ITEM}
                     formatter={(value, name) => {
                       if (value == null) return ["—", name];
                       if (name === "Ferritin") return [`${value} ng/ml`, "Ferritin"];
                       if (name === "Hb") return [`${value} g/dL`, "Haemoglobin"];
                       if (name === "TS") return [`${value}%`, "Transferrin Sat"];
                       return [value, name];
                     }} />
            <Legend wrapperStyle={LEGEND} iconType="circle" />
            {hasFer && (
              <Bar yAxisId="fer" dataKey="ferritin" name="Ferritin"
                   fill="#8B5CF6" fillOpacity={0.6} stroke="#8B5CF6"
                   maxBarSize={32} radius={[3, 3, 0, 0]} />
            )}
            {hasHb && (
              <Line yAxisId="hb" type="monotone" dataKey="hb" name="Hb"
                    stroke="#EF4444" strokeWidth={2.5}
                    dot={HB_DOT} activeDot={HB_ACTIVE}
                    connectNulls isAnimationActive={false} />
            )}
            {hasTs && (
              <Line yAxisId="ts" type="monotone" dataKey="ts" name="TS"
                    stroke="#F59E0B" strokeWidth={2} strokeDasharray="4 3"
                    dot={TS_DOT} activeDot={HB_ACTIVE}
                    connectNulls isAnimationActive={false} />
            )}
          </ComposedChart>
        </ChartContainer>
      </div>
    </div>
  );
}
